import type { WovarCustomer, WovarProduct } from "@commercekit/api";
import { useErrorAlert, type HTMLComponent } from "@commercekit/react";
import { useCart, useLocale, useLocalePath } from "@commercekit/remix/index";
import {
  fallbackMoney,
  formatMoneyWithCurrency,
  taxExemptedCustomer,
} from "@commercekit/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import { useWvSelectItem } from "~/hooks/useDataLayer";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import { optimizeImageUrl } from "~/lib/imageUtils";
import WvLink from "../atoms/WvLink";
import WvRelatedProductActionButton from "./WvRelatedProductActionButton";

const WvRelatedProductItem: HTMLComponent<{
  product: WovarProduct;
  isAdded?: boolean;
  itemListId: string;
  itemListName: string;
  customer: WovarCustomer;
}> = ({ product, isAdded = false, itemListId, itemListName, customer }) => {
  const { localeFullName } = useLocale();
  const { $pt } = usePrismicLabels();
  const localePath = useLocalePath();
  const gtmSelectItem = useWvSelectItem();
  const { show } = useErrorAlert();
  const [waitingForSuccess, setWaitingForSuccess] = useState(false);
  const [addToCartSuccess, setAddToCartSuccess] = useState(false);
  const wasAddingRef = useRef(false);

  const { loadingQueue, cartLoading, resultQueue, clearResult } = useCart();

  // Track when cart loading starts for this product
  const cartLoadingForProduct = useMemo(
    () => loadingQueue.find((i) => i.id === product.id) && cartLoading,
    [loadingQueue, cartLoading, product.id]
  );

  const cartError = useMemo(() => {
    if (!resultQueue?.length) return null;
    const error = resultQueue.find((result) => result.actionFail);
    return error?.error ? error.error : null;
  }, [resultQueue]);

  // Set waitingForSuccess to true when loading starts
  useEffect(() => {
    if (cartLoadingForProduct) {
      setWaitingForSuccess(true);
    }
  }, [cartLoadingForProduct]);

   // Clear waitingForSuccess when item is confirmed added, on error, or after success with delay
  useEffect(() => {
    if (!waitingForSuccess) return;

    if (isAdded) {
      // Item confirmed in cart - safe to clear immediately
      setWaitingForSuccess(false);
      return;
    }

    if (!cartLoadingForProduct) {
      if (cartError) {
        // Loading finished with error - clear immediately
        setWaitingForSuccess(false);
      } else {
        // Loading finished successfully - wait briefly for isAdded to update from parent
        const timeoutId = setTimeout(() => setWaitingForSuccess(false), 50);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [waitingForSuccess, isAdded, cartLoadingForProduct, cartError]);

  const isLoading = useMemo(
    () => Boolean(cartLoadingForProduct) || (waitingForSuccess && !isAdded),
    [cartLoadingForProduct, waitingForSuccess, isAdded]
  );

  useEffect(() => {
    if (!cartLoading && resultQueue?.length) {
      clearResult();
      if (cartError) {
        show(cartError, "warning");
      }
    }
  }, [resultQueue, cartLoading, clearResult, show, cartError]);

  // Callers that don't track cart contents (the PDP related block) get a
  // short-lived checkmark after adding; the add-to-cart popup keeps its
  // persistent `isAdded` state.
  useEffect(() => {
    if (isLoading) {
      wasAddingRef.current = true;
      setAddToCartSuccess(false);
      return;
    }
    if (wasAddingRef.current) {
      wasAddingRef.current = false;
      if (cartError) return;
      setAddToCartSuccess(true);
      const timeoutId = setTimeout(() => setAddToCartSuccess(false), 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [isLoading, cartError]);

  return (
    <>
      <div className="px-2 col-related col-lg-4 col-xl-3 mb-lg-3">
        <div className="text-left relatedblock h-100">
          <WvLink
            to={localePath(`/${product?.slug}`)}
            onClick={() => gtmSelectItem(product, itemListId, itemListName)}
            className="py-3 d-flex flex-column justify-content-between h-100"
          >
            <span className="mb-2 d-block relatedblock__image img">
              <img
                src={optimizeImageUrl(
                  product?.defaultImage?.urlThumbnail ||
                  product?.images?.[0]?.urlThumbnail ||
                  ""
                )}
                loading="lazy"
                height="100"
                width="100"
                alt={decodeURIComponent(product?.defaultImage?.altText || "")}
              />
            </span>
            <span className="mb-2 d-block relatedblock__title heading sm sans-serif strong mb-lg-3">
              <div dangerouslySetInnerHTML={{ __html: product?.name }}></div>
            </span>
            <span className="row no-gutters">
              <span className="d-block relatedblock__price col">
                {product?.salePriceInclTax ? (
                  <span className="product-price-inner sale">
                    <span
                      className="mr-2 price-old"
                      data-price={product?.priceInclTax?.value}
                    >
                      {formatMoneyWithCurrency(
                        localeFullName,
                        product?.priceInclTax?.currencyCode
                          ? product?.priceInclTax
                          : fallbackMoney()
                      )}
                    </span>
                    <span
                      className="price"
                      data-price={product?.salePriceInclTax?.value}
                    >
                      {formatMoneyWithCurrency(
                        localeFullName,
                        product?.salePriceInclTax?.currencyCode
                          ? product?.salePriceInclTax
                          : fallbackMoney()
                      )}
                    </span>
                    {!taxExemptedCustomer(customer?.type) ? (
                      <span className="mt-1 ex-btw">
                        <span
                          className="d-inline"
                          data-price={product?.salePriceExclTax?.value}
                        >
                          {formatMoneyWithCurrency(
                            localeFullName,
                            product?.salePriceExclTax?.currencyCode
                              ? product?.salePriceExclTax
                              : fallbackMoney()
                          )}
                        </span>{" "}
                        {$pt("product_label_excluding_tax")}
                      </span>
                    ) : (
                      <span className="mt-1 ex-btw">
                        {$pt("product_label_tax_exempted_price")}
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="product-price-inner">
                    <span
                      className="price"
                      data-price={product?.priceInclTax?.value}
                    >
                      {formatMoneyWithCurrency(
                        localeFullName,
                        product?.priceInclTax?.currencyCode
                          ? product?.priceInclTax
                          : fallbackMoney()
                      )}
                    </span>
                    {!taxExemptedCustomer(customer?.type) ? (
                      <span className="mt-1 ex-btw">
                        <span
                          className="d-inline"
                          data-price={product?.priceExclTax?.value}
                        >
                          {formatMoneyWithCurrency(
                            localeFullName,
                            product?.priceExclTax?.currencyCode
                              ? product?.priceExclTax
                              : fallbackMoney()
                          )}
                        </span>{" "}
                        {$pt("product_label_excluding_tax")}
                      </span>
                    ) : (
                      <span className="mt-1 ex-btw">
                        {$pt("product_label_tax_exempted_price")}
                      </span>
                    )}
                  </span>
                )}
              </span>
              {product?.unitsOfMeasure[0]?.availability?.canAddToCart && (
                <span className="col-auto relatedblock__addtocart-wrapper">
                  {isAdded || addToCartSuccess ? (
                        <div className="related-product-success-state">
                          <i className="related-product-success-icon large" data-icon="&#xf26b;"></i>
                        </div>
                      ) : isLoading ? (
                        <div className="related-product-loading-state">
                          <i className="related-product-loading-icon zmdi-hc-spin large" data-icon-after=""></i>
                        </div>
                      ) : (
                        <WvRelatedProductActionButton
                          product={product}
                          isSuccess={isAdded}
                        />
                      )}
                </span>
              )}
            </span>
          </WvLink>
        </div>
      </div>
    </>
  );
};

export default WvRelatedProductItem;
